import type { components } from "@/api/generated/openapi";
import { flagIsListed } from "@/lib/plans/flags";

/** What `GET /api/v1/access` published about this Workspace. The contract is closed. */
export type AccessAnswer = components["schemas"]["AccessOut"];

/** What one Plan gives one Feature: on or off, a numeric allowance, or no limit at all. */
export type FeatureValue = AccessAnswer["features"][string];

export function isFreePlan(plan: AccessAnswer["plan"]): boolean {
  return plan === "collector_free" || plan === "vendor_free";
}

/**
 * Where one Feature stands for this Workspace, read from both published maps.
 *
 * - `unknown` — no answer has arrived, so neither map can be consulted.
 * - `unreleased` — the answer did arrive and its flag is off. The Feature does not exist yet and
 *   the server refuses it, so there is nothing to sell.
 * - `locked` — it exists, and this Workspace's Plan does not include it.
 * - `included` — it exists and the Plan includes it.
 *
 * `unknown` and `unreleased` both mean "show nothing", but they are not one state: a surface that
 * wants to wait for the answer can tell them apart, and collapsing them is how a blipped fetch
 * comes to look like a withdrawn Feature.
 */
export type FeatureAccess = "unknown" | "unreleased" | "locked" | "included";

/**
 * Where `feature` stands, given the answer this Workspace has, or `null` when it has none.
 *
 * No coded default is consulted here. A flag's default answers "what does this client do when
 * nothing has told it", and with no answer the Plan is unknown too, so the Feature question has
 * no answer to default to. Reading a stale or absent answer as a Plan is how a Free Workspace
 * comes to see what a paid one bought.
 *
 * The Feature's value is read for truthiness, never against `true`: an allowance of `500` and
 * `"unlimited"` both include it, and `false` and `0` both withhold it.
 */
export function featureAccess(answer: AccessAnswer | null, feature: string): FeatureAccess {
  if (answer === null) return "unknown";
  if (!flagIsListed(answer.flags, feature)) return "unreleased";
  return answer.features[feature] ? "included" : "locked";
}

/**
 * A usage reset instant as its calendar date, "November 1". Allowances reset at 00:00 UTC, so a
 * local date would read the day before in the US.
 */
/** A limit or usage count with a thousands separator, "2,000". */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

export function resetDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
